import { getConversionFactorValue } from '@shared/helpers/conversion-factors.helpers';
import { buildElementPathIndex } from '@shared/helpers/pipeline/batch-update.helpers';
import { buildResultsRecordFromProposalComputeRecord } from '@shared/helpers/pipeline/build-results-record.helpers';
import {
  computeVersionForProposal,
  type ComputeVersionDependencies,
} from '@shared/helpers/pipeline/compute-pipeline.helpers';
import {
  ProjectChangeType,
  type Action,
} from '@shared/helpers/pipeline/update-engine.models';
import type { IProposal } from '@shared/models/proposals.model';
import type { Baseline, Project } from '@shared/models/project.model';
import type { Results, ResultsRecord } from '@shared/models/unit.model';
import { cacheFactory } from '@shared/helpers/function.helpers';
import {
  buildRecipeLookupForStamp,
  omitStampedOnlyResultsRows,
  stampMissingRecipeChildrenInVersion,
} from '@shared/helpers/stamp-missing-recipe-children.helpers';
import {
  type ProposalElementRollupContext,
  computePerActionKpiDeltaShare,
} from './diff-view.helpers';
import { getChangeAttributionHostElementId } from './action.helpers';
import { resolveVersionForProposal } from './resolve-version-for-proposal.helpers';
import { getResolvedVersionEnrichCacheDeps } from './resolved-version-enrich-cache.helpers';
import {
  getResultsScopeCacheKey,
  type ResultsScope,
} from '../results-scope.helpers';

export type PerActionKpiPrefixReadOptions = {
  /**
   * Stored (unscoped) baseline {@link ResultsRecord}. {@link filterResults} is applied
   * inside the prefix reader — passing an already-scoped record (e.g. KPI-card rows)
   * double-filters and can collapse prefix 0 when an SBEF subset is active.
   */
  baselineRecord: ResultsRecord;
  /**
   * Result scope (lifecycle / SBEF / conservative) applied to each prefix row
   * before GFA normalize. Omit to read stored rows unchanged.
   */
  filterResults?: (results: Results) => Results;
  normalizePerGfa: (results: Results) => Results;
  /**
   * Enrich dependencies for the prefix compute passes. Required — the prefix
   * series runs the real compute pipeline, so omitting connected data tables
   * makes the DEV-3421 fail-fast throw `MissingConnectedDataTablesError`
   * (Sentry NODON-UI-3XT), and omitting weather hours silently computes heat
   * demand from no weather data.
   */
  computeDependencies: ComputeVersionDependencies;
};

export type PerActionKpiDeltasWithSignCorrection = {
  co2eByActionId: Record<string, number>;
  sekByActionId: Record<string, number> | null;
};

const EPS = 1e-9;

const PRODUCT_CARBON_CHANGE_AFTER_RECIPE = new Set<ProjectChangeType>([
  ProjectChangeType.ProductElementEpd,
  ProjectChangeType.ProductElementCarbonLevelOverride,
]);

/**
 * Passthrough proposal for {@link computeVersionForProposal} when actions are
 * already merged onto the resolved version tree.
 */
const toPassthroughProposal = (proposal: IProposal): IProposal => ({
  id: proposal.id,
  name: proposal.name,
  colorIndex: proposal.colorIndex,
  actionIds: proposal.actionIds,
  resultsRecord: {},
});

const readVersionKpisPerGfa = (
  resultsRecord: ResultsRecord,
  versionId: string,
  options: PerActionKpiPrefixReadOptions,
): { co2e: number; sek: number } => {
  const rawRow = resultsRecord[versionId] ?? {};
  const row = options.filterResults ? options.filterResults(rawRow) : rawRow;
  const normalized = options.normalizePerGfa(row);
  return {
    co2e: getConversionFactorValue(normalized, 'co2e'),
    sek: getConversionFactorValue(normalized, 'sek'),
  };
};

type PrefixKpis = { co2e: number; sek: number };

export type SequentialMarginalKpis = {
  co2eByActionId: Record<string, number>;
  sekByActionId: Record<string, number>;
};

const createPrefixSeriesComputeContext = (
  project: Project,
  version: Baseline,
  proposal: IProposal,
  options: PerActionKpiPrefixReadOptions,
) => ({
  project,
  version,
  proposal,
  options,
  // Built once for the whole series, like step 5b does per version: the merge is proportional to
  // the recipe catalog while a healthy project needs no stamping at all.
  //
  // No catalog argument, on purpose. Prefix 0 is read from `options.baselineRecord`, which always
  // comes from the enrich that runs while the user works — and that path leaves
  // `EnrichProjectData.recipes` empty because the field is export-only, so step 5b stamps from the
  // version's embedded recipes alone. Passing a store catalog here would let prefixes 1..n stamp
  // hosts prefix 0 could not, and the first action would absorb the difference (DEV-3656/DEV-3666).
  recipeLookup: buildRecipeLookupForStamp(version),
  products: options.computeDependencies.products ?? {},
});

type PrefixSeriesComputeContext = ReturnType<
  typeof createPrefixSeriesComputeContext
>;

/** One full pipeline pass: version-level KPIs after the first {@link prefixActionCount} actions. */
const computePrefixKpis = (
  ctx: PrefixSeriesComputeContext,
  prefixActionCount: number,
): PrefixKpis => {
  const { project, version, proposal, options } = ctx;
  const prefixActionIds = proposal.actionIds.slice(0, prefixActionCount);
  const prefixProposal: IProposal = {
    ...proposal,
    actionIds: prefixActionIds,
  };
  // resolveVersionForProposal resolves the proposal by id from the version's
  // stored proposals, so the sliced prefix proposal must replace the stored
  // one — otherwise every prefix resolves the full proposal and the first
  // action absorbs the whole KPI delta (DEV-3703).
  const { version: resolvedVersion } = resolveVersionForProposal(
    { ...version, proposals: [prefixProposal] },
    prefixProposal,
  );
  // Same stamp as step 5b so every prefix is comparable to the stamped prefix-0 baseline.
  const stamped = stampMissingRecipeChildrenInVersion(
    resolvedVersion,
    ctx.recipeLookup,
    ctx.products,
  );
  const computeRecord = computeVersionForProposal(
    project,
    stamped.version,
    toPassthroughProposal(prefixProposal),
    options.computeDependencies,
  );
  const resultsRecord = omitStampedOnlyResultsRows(
    buildResultsRecordFromProposalComputeRecord(
      computeRecord,
      stamped.version,
      project,
      buildElementPathIndex(stamped.version),
    ),
    stamped.stampedHostIds,
  );
  return readVersionKpisPerGfa(resultsRecord, version.id, options);
};

const marginalsFromPrefixSeries = (
  series: readonly PrefixKpis[],
  actionIds: readonly string[],
): SequentialMarginalKpis => {
  const co2eByActionId: Record<string, number> = {};
  const sekByActionId: Record<string, number> = {};

  for (let i = 0; i < actionIds.length; i++) {
    const actionId = actionIds[i];
    if (!actionId) continue;
    const prev = series[i] ?? { co2e: 0, sek: 0 };
    const next = series[i + 1] ?? prev;
    co2eByActionId[actionId] = next.co2e - prev.co2e;
    sekByActionId[actionId] = next.sek - prev.sek;
  }

  return { co2eByActionId, sekByActionId };
};

/**
 * Version-level CO2e and SEK per GFA after each proposal prefix (index 0 = baseline).
 * One pipeline pass per non-zero prefix; {@link PerActionKpiPrefixReadOptions.filterResults}
 * should match the KPI card's result scope.
 */
export const buildProposalPrefixVersionKpisPerGfaSeries = (
  project: Project,
  version: Baseline,
  proposal: IProposal,
  options: PerActionKpiPrefixReadOptions,
): PrefixKpis[] => {
  const ctx = createPrefixSeriesComputeContext(
    project,
    version,
    proposal,
    options,
  );
  const series: PrefixKpis[] = [
    readVersionKpisPerGfa(options.baselineRecord, version.id, options),
  ];
  for (
    let prefixActionCount = 1;
    prefixActionCount <= proposal.actionIds.length;
    prefixActionCount++
  ) {
    series.push(computePrefixKpis(ctx, prefixActionCount));
  }
  return series;
};

/**
 * Per-action marginal CO2e and SEK deltas in proposal order (one prefix series).
 */
export const computeSequentialMarginalPerActionKpis = (
  project: Project,
  version: Baseline,
  proposal: IProposal,
  options: PerActionKpiPrefixReadOptions,
): SequentialMarginalKpis =>
  marginalsFromPrefixSeries(
    buildProposalPrefixVersionKpisPerGfaSeries(
      project,
      version,
      proposal,
      options,
    ),
    proposal.actionIds,
  );

export type SequentialMarginalKpiStepper = {
  /** Pipeline passes left before {@link getMarginals} is valid. */
  remainingSteps: () => number;
  /** Runs exactly one prefix pipeline pass. */
  step: () => 'done' | 'pending';
  /** Marginal per-action KPIs; only valid once {@link step} has returned `'done'`. */
  getMarginals: () => SequentialMarginalKpis;
};

/**
 * Incremental variant of {@link computeSequentialMarginalPerActionKpis}: each {@link step}
 * runs one prefix pipeline pass, so a caller can yield to the event loop between passes
 * instead of blocking the main thread for the whole series — with many actions the full
 * series takes tens of seconds and froze the app (DEV-3703).
 */
export const createSequentialMarginalKpiStepper = (
  project: Project,
  version: Baseline,
  proposal: IProposal,
  options: PerActionKpiPrefixReadOptions,
): SequentialMarginalKpiStepper => {
  const ctx = createPrefixSeriesComputeContext(
    project,
    version,
    proposal,
    options,
  );
  const series: PrefixKpis[] = [
    readVersionKpisPerGfa(options.baselineRecord, version.id, options),
  ];
  const totalPrefixes = proposal.actionIds.length;

  return {
    remainingSteps: () => totalPrefixes - (series.length - 1),
    step: () => {
      if (series.length <= totalPrefixes) {
        series.push(computePrefixKpis(ctx, series.length));
      }
      return series.length > totalPrefixes ? 'done' : 'pending';
    },
    getMarginals: () => marginalsFromPrefixSeries(series, proposal.actionIds),
  };
};

/**
 * True when element-attributed KPI scaling is unreliable and prefix marginals
 * should be used instead (DEV-3381, DEV-3389, recipe + follow-up on same host).
 */
export const proposalNeedsSequentialKpiSignCorrection = (
  proposal: IProposal,
  actions: readonly Action[],
): boolean => {
  const actionsById = new Map(actions.map((action) => [action.id, action]));
  const recipeAppliedHosts = new Set<string>();

  for (const actionId of proposal.actionIds) {
    const action = actionsById.get(actionId);
    if (!action) continue;

    for (const change of action.changes) {
      if (change.type === ProjectChangeType.ElementRecipeApply) {
        const host = getChangeAttributionHostElementId(change);
        if (host) recipeAppliedHosts.add(host);
        continue;
      }

      if (
        recipeAppliedHosts.size > 0 &&
        PRODUCT_CARBON_CHANGE_AFTER_RECIPE.has(change.type)
      ) {
        return true;
      }

      const host = getChangeAttributionHostElementId(change);
      if (host && recipeAppliedHosts.has(host)) return true;
    }
  }

  return false;
};

/**
 * Replaces attributed shares whose sign disagrees with the sequential marginal,
 * then rescales the remaining actions so the sum still matches {@link kpiDelta}.
 */
export const applySequentialMarginalSignCorrection = (
  attributed: Readonly<Record<string, number>>,
  marginals: Readonly<Record<string, number>>,
  actionIds: readonly string[],
  kpiDelta: number,
): Record<string, number> => {
  const out: Record<string, number> = { ...attributed };
  const correctedIds = new Set<string>();

  for (const id of actionIds) {
    const attributedValue = attributed[id] ?? 0;
    const marginal = marginals[id] ?? 0;
    if (Math.abs(marginal) < EPS) continue;
    if (attributedValue * marginal >= 0) continue;
    out[id] = marginal;
    correctedIds.add(id);
  }

  if (correctedIds.size === 0) return attributed;

  const correctedSum = [...correctedIds].reduce(
    (sum, id) => sum + (out[id] ?? 0),
    0,
  );
  const remainingDelta = kpiDelta - correctedSum;
  const flexibleIds = actionIds.filter((id) => !correctedIds.has(id));

  if (flexibleIds.length === 0) return out;

  const flexibleAttributedSum = flexibleIds.reduce(
    (sum, id) => sum + (attributed[id] ?? 0),
    0,
  );

  if (Math.abs(flexibleAttributedSum) < EPS) {
    const flexMarginalSum = flexibleIds.reduce(
      (sum, id) => sum + (marginals[id] ?? 0),
      0,
    );
    if (Math.abs(flexMarginalSum) >= EPS) {
      const factor = remainingDelta / flexMarginalSum;
      for (const id of flexibleIds) {
        out[id] = (marginals[id] ?? 0) * factor;
      }
      return out;
    }
    const each = remainingDelta / flexibleIds.length;
    for (const id of flexibleIds) out[id] = each;
    return out;
  }

  const factor = remainingDelta / flexibleAttributedSum;
  for (const id of flexibleIds) {
    out[id] = (attributed[id] ?? 0) * factor;
  }
  return out;
};

const marginalSum = (
  marginals: Readonly<Record<string, number>>,
  actionIds: readonly string[],
): number => actionIds.reduce((sum, id) => sum + (marginals[id] ?? 0), 0);

const applySignCorrectionWhenMarginalsComparable = (
  attributed: Record<string, number>,
  marginals: Record<string, number>,
  actionIds: readonly string[],
  kpiDelta: number,
): Record<string, number> => {
  // Client prefix recompute can yield ~0 total marginal while enriched
  // element-attributed shares are still meaningful — keep attributed (UI shows
  // values instead of "-") until marginals are comparable to kpiDelta.
  if (Math.abs(marginalSum(marginals, actionIds)) < EPS) return attributed;

  return applySequentialMarginalSignCorrection(
    attributed,
    marginals,
    actionIds,
    kpiDelta,
  );
};

type PerActionKpiDeltaShareOptions = {
  project: Project;
  version: Baseline;
  proposal: IProposal;
  actions: Action[];
  baselineRecord: ResultsRecord;
  proposalRecord: ResultsRecord;
  rollupContext?: ProposalElementRollupContext;
  prefixReadOptions: PerActionKpiPrefixReadOptions;
};

type PerActionKpiDeltasComputeOptions = PerActionKpiDeltaShareOptions & {
  kpiCo2Delta: number;
  kpiSekDelta: number | null;
  /**
   * Display scope used by `prefixReadOptions.filterResults`. Passed as data, not
   * as the filter callback, so URL-sync callback identity does not bust the cache.
   */
  resultScope?: ResultsScope;
};

/**
 * Element-attributed KPI shares only — no prefix pipeline pass, cheap enough for the
 * render path. Sign correction (when needed) is applied separately with
 * {@link applyMarginalsToAttributedKpiDeltas} so the expensive series can run off
 * the render path (DEV-3703).
 */
export const computeAttributedPerActionKpiDeltas = (
  options: Omit<PerActionKpiDeltasComputeOptions, 'prefixReadOptions'>,
): PerActionKpiDeltasWithSignCorrection => ({
  co2eByActionId: computePerActionKpiDeltaShare(
    options.proposal,
    options.actions,
    options.baselineRecord,
    options.proposalRecord,
    options.kpiCo2Delta,
    'deltaCo2e',
    options.rollupContext,
  ),
  sekByActionId:
    options.kpiSekDelta === null
      ? null
      : computePerActionKpiDeltaShare(
          options.proposal,
          options.actions,
          options.baselineRecord,
          options.proposalRecord,
          options.kpiSekDelta,
          'deltaCost',
          options.rollupContext,
        ),
});

/**
 * Applies sequential prefix marginals onto attributed shares: sign-flipped actions take
 * the marginal, the rest are rescaled so the sum still matches the KPI deltas.
 */
export const applyMarginalsToAttributedKpiDeltas = (
  attributed: PerActionKpiDeltasWithSignCorrection,
  marginals: SequentialMarginalKpis,
  options: {
    actionIds: readonly string[];
    kpiCo2Delta: number;
    kpiSekDelta: number | null;
  },
): PerActionKpiDeltasWithSignCorrection => ({
  co2eByActionId: applySignCorrectionWhenMarginalsComparable(
    attributed.co2eByActionId,
    marginals.co2eByActionId,
    options.actionIds,
    options.kpiCo2Delta,
  ),
  sekByActionId:
    attributed.sekByActionId === null || options.kpiSekDelta === null
      ? null
      : applySignCorrectionWhenMarginalsComparable(
          attributed.sekByActionId,
          marginals.sekByActionId,
          options.actionIds,
          options.kpiSekDelta,
        ),
});

/**
 * Uncached prefix + attributed KPI split. {@link computePerActionKpiDeltasWithSignCorrection}
 * memoizes this per proposal — sequential correction runs one pipeline pass per action.
 */
const computePerActionKpiDeltasWithSignCorrectionUncached = (
  options: PerActionKpiDeltasComputeOptions,
): PerActionKpiDeltasWithSignCorrection => {
  const attributed = computeAttributedPerActionKpiDeltas(options);

  if (
    !proposalNeedsSequentialKpiSignCorrection(options.proposal, options.actions)
  ) {
    return attributed;
  }

  const marginals = computeSequentialMarginalPerActionKpis(
    options.project,
    options.version,
    options.proposal,
    options.prefixReadOptions,
  );

  return applyMarginalsToAttributedKpiDeltas(attributed, marginals, {
    actionIds: options.proposal.actionIds,
    kpiCo2Delta: options.kpiCo2Delta,
    kpiSekDelta: options.kpiSekDelta,
  });
};

/**
 * CO2e and cost per-action KPI shares with one prefix pipeline pass for marginals.
 *
 * Cached per version id + proposal id via {@link cacheFactory} so swapping back to a
 * proposal (and a second render in the same click) skips the prefix series. Deps omit
 * `version` / `project` object identity and callback `filterResults` / `normalizePerGfa` —
 * those change on URL sync without new inputs. Scope (lifecycle/SBEF/conservative) is
 * a primitive token; persisted `resultsRecord` refs invalidate when pipeline results
 * are replaced, not when a store clone keeps the same records. Rollup presence is a
 * token so a no-tree call cannot reuse shares computed from the proposal-visible tree.
 */
export const computePerActionKpiDeltasWithSignCorrection = (
  options: PerActionKpiDeltasComputeOptions,
): PerActionKpiDeltasWithSignCorrection => {
  const { version, proposal, project, prefixReadOptions } = options;
  return cacheFactory(
    () => computePerActionKpiDeltasWithSignCorrectionUncached(options),
    `perActionKpi[${version.id}][${proposal.id}]`,
    [
      version.actions,
      proposal.actionIds,
      version.resultsRecord,
      proposal.resultsRecord,
      options.kpiCo2Delta,
      options.kpiSekDelta,
      getResultsScopeCacheKey(options.resultScope),
      options.rollupContext ? 'with-rollup' : 'no-rollup',
      ...getResolvedVersionEnrichCacheDeps(project),
      prefixReadOptions.computeDependencies.dataTables ?? null,
      prefixReadOptions.computeDependencies.weatherHours ?? null,
    ],
  );
};
